import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth/session';
import { useAcceptGuardianInvitation } from '@/features/guardian/hooks';
import {
  clearPendingInvitationToken,
  getPendingInvitationToken,
  setPendingInvitationToken,
} from '@/features/guardian/pending-token';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import { ApiError } from '@/lib/api/client';

const GENERIC_INVALID_MESSAGE =
  'Tautan undangan tidak valid, sudah digunakan, sudah dicabut, atau tidak sesuai dengan akun yang sedang masuk.';

/**
 * The Guardian invitation acceptance route (ADR-025's Addendum, Decision
 * D/K: authentication alone gates redemption - never a prior organization
 * membership, since acceptance is what may create one). Captures `?token=`
 * on first load, stores it under its own sessionStorage key so it survives
 * a login/register redirect, and immediately scrubs it from the URL/history
 * (Phase G-0 Job 11).
 */
export function AcceptInvitationPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { status, logout, refreshUser } = useAuth();
  const { select } = useActiveOrganization();
  const accept = useAcceptGuardianInvitation();
  const attempted = useRef(false);
  const [missingToken, setMissingToken] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken !== null && urlToken.length > 0) {
      setPendingInvitationToken(urlToken);
      void navigate('/guardian-invitations/accept', { replace: true });
      return;
    }

    if (getPendingInvitationToken() === null) {
      setMissingToken(true);
    }
    // Only runs once per mount: the URL token, once captured, is
    // immediately scrubbed above, so this effect must not re-fire on the
    // resulting navigation and treat the now-clean URL as "missing".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== 'authenticated' || attempted.current) {
      return;
    }
    const token = getPendingInvitationToken();
    if (token === null) {
      return;
    }
    attempted.current = true;
    accept.mutate(token, {
      onSuccess: (result) => {
        clearPendingInvitationToken();
        // Re-resolve membership truthfully via /auth/me first (ADR-026
        // Addendum, AO-13) - only once the refreshed membership list
        // actually contains the newly-granted organization does `select`
        // accept it (AO-15: never trust an id that isn't a current
        // membership). The accept response's own `organizationId` is a
        // genuine backend field (server/src/services/guardian/guardian.service.ts's
        // `StudentGuardianView`), not guessed. Best-effort: a transient
        // refresh failure must not undo a successful acceptance - the
        // user still lands on a normal AO-4/AO-5 outcome next time
        // memberships are read.
        refreshUser()
          .then(() => {
            select(result.organizationId);
          })
          .catch(() => {
            // Intentionally ignored - see comment above.
          });
      },
      // Deliberately not cleared on error - see the module doc comment:
      // the backend collapses invalid/used/revoked and email-mismatch into
      // one generic code, so an email mismatch (recoverable by logging
      // into the correct account) must not lose the still-valid token.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (missingToken) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Tautan Tidak Ditemukan</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{GENERIC_INVALID_MESSAGE}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Skeleton className="h-8 w-48" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Masuk untuk Melanjutkan</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Masuk atau daftar dengan email yang menerima undangan untuk melanjutkan.
            </p>
            <div className="flex gap-2">
              <Button asChild>
                <Link to="/login" state={{ from: '/guardian-invitations/accept' }}>
                  Masuk
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/register" state={{ from: '/guardian-invitations/accept' }}>
                  Daftar
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accept.isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Undangan Diterima</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert>
              <AlertTitle>Berhasil</AlertTitle>
              <AlertDescription>Anda kini terhubung sebagai wali.</AlertDescription>
            </Alert>
            <Button
              onClick={() => {
                void navigate('/guardians/me');
              }}
            >
              Lihat Anak Saya
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accept.isError) {
    const message =
      accept.error instanceof ApiError && accept.error.code === 'GUARDIAN_INVITATION_INVALID'
        ? GENERIC_INVALID_MESSAGE
        : 'Terjadi kesalahan. Silakan coba lagi.';

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Tidak Dapat Menerima Undangan</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert variant="destructive">
              <AlertDescription>{message}</AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground">
              Jika undangan ini ditujukan untuk akun lain, keluar dan masuk dengan akun yang sesuai.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                void logout();
              }}
            >
              Keluar dan Gunakan Akun Lain
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4" role="status">
      <Skeleton className="h-8 w-48" />
    </div>
  );
}
